import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronRight } from 'lucide-react'

const TRUSTED_BY = [
  'Kennedy University',
  'Dunster Business School',
  'LSBS',
]

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
  }),
}

export default function LandingPage() {
  const navigate = useNavigate()

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'var(--color-void)' }}
    >
      {/* Grid background overlay */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      {/* Radial glow behind hero */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(37,99,235,0.06) 0%, transparent 70%)',
        }}
      />

      {/* ── Navbar ── */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto w-full">
        {/* Wordmark */}
        <div className="flex items-baseline gap-0.5">
          <span
            className="text-xl font-sans font-semibold tracking-tight"
            style={{ color: 'var(--color-text)' }}
          >
            Acd
            <span style={{ color: 'var(--color-gold)' }}>yON</span>
          </span>
          <span
            className="text-xl italic ml-1.5"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text)',
            }}
          >
            Scholar
          </span>
        </div>

        {/* Nav actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 text-sm font-sans font-medium rounded-md border transition-colors duration-200"
            style={{
              color: 'var(--color-text-muted)',
              borderColor: 'var(--color-border)',
              background: 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--color-text)'
              e.currentTarget.style.borderColor = 'var(--color-gold-muted)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--color-text-muted)'
              e.currentTarget.style.borderColor = 'var(--color-border)'
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => navigate('/register')}
            className="px-4 py-2 text-sm font-sans font-semibold rounded-md transition-all duration-200"
            style={{
              background: 'var(--color-gold)',
              color: 'var(--color-void)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--color-gold-light)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--color-gold)'
            }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
        {/* Eyebrow */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.1}
          className="text-xs tracking-[0.3em] font-mono font-medium mb-8 uppercase"
          style={{ color: 'var(--color-gold)' }}
        >
          Doctoral Writing Platform
        </motion.p>

        {/* H1 */}
        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.25}
          className="leading-[1.1] mb-3"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(52px, 8vw, 72px)',
            color: 'var(--color-text)',
          }}
        >
          Your PhD.
        </motion.h1>

        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.35}
          className="italic leading-[1.1] mb-10"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(52px, 8vw, 72px)',
          }}
        >
          <span className="gold-shimmer">Written to last.</span>
        </motion.h1>

        {/* Subtext */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.45}
          className="max-w-lg mx-auto text-base font-sans font-light leading-relaxed mb-12"
          style={{ color: 'var(--color-text-muted)' }}
        >
          AcdyON Scholar generates every document in your doctoral journey —
          from Statement of Purpose to final thesis — at the quality standard
          of a seasoned academic supervisor.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.55}
          className="flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            onClick={() => navigate('/register')}
            className="flex items-center gap-2 px-7 py-3.5 text-sm font-sans font-semibold rounded-md transition-all duration-200 group"
            style={{
              background: 'var(--color-gold)',
              color: 'var(--color-void)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--color-gold-light)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--color-gold)'
            }}
          >
            Start for Free
            <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>

          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-7 py-3.5 text-sm font-sans font-medium rounded-md border transition-all duration-200 group"
            style={{
              color: 'var(--color-text-muted)',
              borderColor: 'var(--color-border)',
              background: 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--color-text)'
              e.currentTarget.style.borderColor = 'var(--color-gold-muted)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--color-text-muted)'
              e.currentTarget.style.borderColor = 'var(--color-border)'
            }}
          >
            See How It Works
            <ChevronRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </motion.div>
      </main>

      {/* ── Pricing ── */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0.65}
        className="relative z-10 py-20 px-6"
        id="pricing"
      >
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-xs tracking-[0.3em] font-mono font-medium mb-3 uppercase"
            style={{ color: 'var(--color-gold)' }}>Pricing</p>
          <h2 className="text-center mb-14"
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 48px)', color: 'var(--color-text)' }}>
            Simple, transparent pricing
          </h2>
          <div className="flex justify-center">
            <div className="relative rounded-2xl p-8 w-full max-w-sm"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', boxShadow: '0 4px 24px rgba(12,22,41,0.06)' }}>
              {/* Popular badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full text-xs font-mono font-semibold"
                  style={{ background: 'var(--color-gold)', color: '#FFFFFF' }}>
                  SCHOLAR PLAN
                </span>
              </div>
              <div className="text-center mb-8">
                <div className="flex items-end justify-center gap-1">
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 56, lineHeight: 1, color: 'var(--color-text)' }}>$19</span>
                  <span className="mb-2 font-sans text-sm" style={{ color: 'var(--color-text-muted)' }}>/user/month</span>
                </div>
                <p className="font-sans text-xs mt-1" style={{ color: 'var(--color-text-faint, #8EA4BC)' }}>
                  Billed monthly · Cancel anytime
                </p>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Unlimited SOP & Synopsis generation',
                  'Full thesis writing (ToC + chapters)',
                  'Research Assistant — unlimited chats',
                  'Reference manager with AI import',
                  'Export to Word & PDF',
                  'Priority academic AI model',
                ].map(feature => (
                  <li key={feature} className="flex items-center gap-2.5 font-sans text-sm"
                    style={{ color: 'var(--color-text-muted)' }}>
                    <span style={{ color: 'var(--color-gold)', fontSize: 16 }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 rounded-xl font-sans font-semibold text-sm transition-all"
                style={{ background: 'var(--color-gold)', color: '#FFFFFF' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9' }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
              >
                Start for Free
              </button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── Trust Strip ── */}
      <motion.footer
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0.7}
        className="relative z-10 border-t py-5 px-8"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-0">
          <span
            className="text-xs font-mono tracking-wide"
            style={{ color: 'var(--color-text-faint, #4A6080)' }}
          >
            Trusted by candidates at
          </span>
          <div className="flex items-center gap-0">
            {TRUSTED_BY.map((name, i) => (
              <span key={name} className="flex items-center">
                <span
                  className="text-xs font-sans font-medium px-3"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {name}
                </span>
                {i < TRUSTED_BY.length - 1 && (
                  <span
                    className="w-px h-3"
                    style={{ background: 'var(--color-border)' }}
                  />
                )}
              </span>
            ))}
          </div>
        </div>
      </motion.footer>
    </div>
  )
}
